import React, { useRef } from 'react';
import { isEqual, cloneDeep } from 'lodash';
import { v4 } from 'uuid';
import { useApiClient } from '@m43/core';
import { logDebug } from '../monitoring/log';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useCallTakerStation } from '../call-taker-station/use-call-taker-station';
import { useEntityPanelManager } from '../panels/entity-panels/monitored-entities/state/entity-panel-manager';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { createStreamListener, useStream } from '../socket/use-stream';
import { PhoneCallsContext } from './phone-calls-context';
import { PhoneCallsStore, phoneCallWithNormalizedPhoneCallDisplay } from './phone-calls-store';
import { PhoneCallAckDto, PhoneCallsResource, AmlPhoneCallDto } from '@mark43/cad-api';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { useViewCallOnMap } from '../event-panel/tabs/ani-ali-e911/use-view-call-on-map';
import { useApplicationType } from '../common/hooks/use-application-type';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { isWirelessPhaseTwo } from './helpers';
import { isCallForStation } from './phone-calls-helpers';
import { useSendAssociatedPhoneNumberIdToMap } from '../event-panel/event-panel-form/hooks/use-send-associated-phone-number-id-to-map';

export function PhoneCallsProvider(props: { children: React.ReactNode }): React.ReactElement {
    const bootstrapState = useBootstrapState();
    const callTakerStation = useCallTakerStation();
    const eventPanelManager = useEntityPanelManager();
    const apiClient = useApiClient();
    const openEventPanel = useOpenEventPanel();
    const sendAssociatedPhoneNumberIdToMap = useSendAssociatedPhoneNumberIdToMap();
    const phoneCallsStore = React.useState<PhoneCallsStore>(
        () =>
            new PhoneCallsStore({
                eventPanelManager,
                initialPhoneCalls: bootstrapState.phoneCallDtos,
            })
    )[0];
    const { trigger } = useIntegratedActionBroadcaster();
    const {
        cadNextDisableLegacyCadAccess,
        preferNewEventsWindow,
        focusMapDuringCallCreation,
        openEventPanelOnE911Call,
        focusEventPanelOnE911Call,
        limitWph2RebidEnabled,
    } = useEnabledFeatures();
    const shouldHandleInCadNextWindow = cadNextDisableLegacyCadAccess || preferNewEventsWindow;
    const viewCallOnMap = useViewCallOnMap();
    const { isFirstResponder, isElectron, isWeb } = useApplicationType();
    const workspaceWindowType = electronBridge
        ? electronBridge.args['--workspace-window-type']
        : undefined;
    const numOpenEventPanels = useWorkspaceStore().activePanels.filter(
        (p) => p.workspacePanelType === 'EVENT' || p.workspacePanelType === 'SCHEDULED_EVENT'
    ).length;

    const previousAmlPhoneCallDtoRef = useRef<AmlPhoneCallDto | undefined>(undefined);

    const amlCallsListener = React.useMemo(() => {
        return createStreamListener({
            streamNames: ['cadNextAml'],
            onMessage: (message) => {
                if (!shouldHandleInCadNextWindow) {
                    return;
                }
                const amlCallDto = message.payload.context;

                if (!isEqual(previousAmlPhoneCallDtoRef.current, amlCallDto)) {
                    logDebug('new amlCall', { phoneNumberId: amlCallDto.phoneNumberId });
                    phoneCallsStore.upsertAmlCall(amlCallDto);

                    if (!isFirstResponder && focusMapDuringCallCreation) {
                        const amlCallView = phoneCallsStore.getAmlCallByPhoneNumberId(
                            amlCallDto.phoneNumberId
                        );

                        viewCallOnMap({ amlCallView, dontFocusOrOpenMap: true });
                    }
                } else {
                    logDebug('duplicate amlCall', { phoneNumberId: amlCallDto.phoneNumberId });
                }
                previousAmlPhoneCallDtoRef.current = amlCallDto;
            },
        });
    }, [
        phoneCallsStore,
        shouldHandleInCadNextWindow,
        focusMapDuringCallCreation,
        viewCallOnMap,
        isFirstResponder,
    ]);

    const ackPhoneCall = React.useCallback(
        (data: PhoneCallAckDto) => {
            return apiClient.request<PhoneCallsResource.AckPhoneCall>({
                method: 'POST',
                url: 'phone_calls/ack/',
                data,
            });
        },
        [apiClient]
    );

    const phoneCallsListener = React.useMemo(() => {
        return createStreamListener({
            streamNames: ['cadNextPhoneCalls'],
            onMessage: (message) => {
                if (
                    !shouldHandleInCadNextWindow ||
                    (isElectron &&
                        workspaceWindowType !== 'EVENT_V2' &&
                        workspaceWindowType !== 'MAP_V2')
                ) {
                    return;
                }
                const phoneCall = message.payload;
                const phoneCallView = phoneCallWithNormalizedPhoneCallDisplay(phoneCall);
                const phoneCallAck: PhoneCallAckDto = {
                    phoneNumberId: phoneCall.phoneNumberId!,
                    phoneCallId: phoneCall.id,
                    uqKey: 'stub-uq-key',
                    sourceRmsEventId: phoneCall.rmsEventId,
                    sourceCallTakerStationId: phoneCall.stationId,
                    feCallTakerStationId: callTakerStation.currentStation?.value,
                    actionsTaken: [],
                };

                logDebug('phoneCall received', {
                    phoneCallId: phoneCall.id,
                    phoneNumberId: phoneCall.phoneNumberId,
                    stationId: phoneCall.stationId,
                    rmsEventId: phoneCall.rmsEventId,
                    classOfService: phoneCall.classOfService,
                    matchedActiveEventId: phoneCall.matchedActiveEventId,
                    streamId: message.messagePosition,
                });

                // duplicate phone call (no update on phone calls)
                if (phoneCallsStore.isDuplicatPhoneCallMessage(phoneCall)) {
                    phoneCallAck.actionsTaken?.push('DUPE_NOOP');
                    ackPhoneCall(phoneCallAck);
                    return;
                } else {
                    phoneCallsStore.upsertPhoneCall(phoneCall);
                }
                if (isFirstResponder) return;

                const isWebOrEventPanelWindow = isWeb || workspaceWindowType === 'EVENT_V2';
                const phoneCallMatchesUsersStation = isCallForStation(
                    phoneCall,
                    callTakerStation.currentStation?.value
                );
                if (!isWebOrEventPanelWindow || !phoneCallMatchesUsersStation) {
                    logDebug('phoneCallAck', {
                        message: 'not event window or not matched station',
                        phoneCallAck,
                        shouldHandleEventPanel: isWebOrEventPanelWindow,
                        phoneCallMatchesUsersStation,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                const isWph2Rebid =
                    phoneCall.classOfService && isWirelessPhaseTwo(phoneCall.classOfService);
                // skip e911 workflow for wph2 rebid (phone number is the same, other fields may be different. Just update the phone call in store without any other actions)
                if (limitWph2RebidEnabled && isWph2Rebid) {
                    logDebug('phoneCallAck', {
                        message: 'limited wph2 rebid',
                        phoneCallAck,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                const matchingOpenEventPanel = phoneCallsStore.getMatchingOpenEventPanel(phoneCall);
                const shouldFocusEventPanel = focusEventPanelOnE911Call || numOpenEventPanels === 0;

                const shouldOpenNewEventPanelForNonWph2 = limitWph2RebidEnabled && !isWph2Rebid;
                // open a new event panel for potential new event, even the phone number has been associated with existing event
                if (
                    matchingOpenEventPanel &&
                    openEventPanelOnE911Call &&
                    shouldOpenNewEventPanelForNonWph2
                ) {
                    phoneCallAck.actionsTaken?.push('CREATE_EMPTY_EVENT');

                    openEventPanel({
                        eventId: undefined,
                        shouldFocusPanel: shouldFocusEventPanel,

                        workspacePanelConfig: {
                            panelState: {
                                phoneCallDto: cloneDeep(phoneCall),
                                initialTab: 'ANI_ALI_RAPIDSOS',
                                originIsE911: true,
                                shouldFocusEventPanelForm: shouldFocusEventPanel,
                            },
                        },
                    });

                    sendAssociatedPhoneNumberIdToMap(phoneCallView.phoneNumberId);

                    if (focusMapDuringCallCreation) {
                        viewCallOnMap({ phoneCallView, dontFocusOrOpenMap: true });
                    }

                    logDebug('phoneCallAck', {
                        message: 'non-wph2 with matched event panel',
                        phoneCallAck,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                // focus on the open event panel that matches the phone number id (event panel already open)
                if (matchingOpenEventPanel && focusEventPanelOnE911Call) {
                    phoneCallAck.actionsTaken?.push('OPEN_EVENT_WINDOW');
                    matchingOpenEventPanel.workspacePanel.focus();
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_TAB',
                            payload: {
                                workspacePanelClientUuid:
                                    matchingOpenEventPanel.workspacePanel.uuid,
                                tab: 'ANI_ALI_RAPIDSOS',
                            },
                        },
                    ]);
                    logDebug('phoneCallAck', {
                        message: 'matched event panel already open',
                        phoneCallAck,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                if (!openEventPanelOnE911Call) {
                    logDebug('phoneCallAck', {
                        message: 'openEventPanelOnE911Call = false',
                        phoneCallAck,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                const matchingActiveNotOpenEventId = phoneCall.matchedActiveEventId;

                // open the event if there is a match on the phone number id (event exists but not open yet)
                if (matchingActiveNotOpenEventId) {
                    const workspacePanelClientUuid = v4();
                    phoneCallAck.actionsTaken?.push('OPEN_EVENT_WINDOW');
                    openEventPanel({
                        eventId: matchingActiveNotOpenEventId,
                        shouldFocusPanel: shouldFocusEventPanel,
                        workspacePanelConfig: {
                            uuid: workspacePanelClientUuid,
                            panelState: {
                                initialTab: 'ANI_ALI_RAPIDSOS',
                                shouldFocusEventPanelForm: shouldFocusEventPanel,
                            },
                        },
                    });

                    sendAssociatedPhoneNumberIdToMap(phoneCallView.phoneNumberId);

                    logDebug('phoneCallAck', {
                        message: 'open existing event panel',
                        phoneCallAck,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                // open a new event panel
                if (!matchingOpenEventPanel && !matchingActiveNotOpenEventId) {
                    phoneCallAck.actionsTaken?.push('CREATE_EMPTY_EVENT');

                    openEventPanel({
                        eventId: matchingActiveNotOpenEventId,
                        shouldFocusPanel: shouldFocusEventPanel,

                        workspacePanelConfig: {
                            panelState: {
                                phoneCallDto: cloneDeep(phoneCall),
                                initialTab: 'ANI_ALI_RAPIDSOS',
                                originIsE911: true,
                                shouldFocusEventPanelForm: shouldFocusEventPanel,
                            },
                        },
                    });

                    sendAssociatedPhoneNumberIdToMap(phoneCallView.phoneNumberId);

                    if (focusMapDuringCallCreation) {
                        viewCallOnMap({ phoneCallView, dontFocusOrOpenMap: true });
                    }
                    logDebug('phoneCallAck', {
                        message: 'no match, open new event panel',
                        phoneCallAck,
                    });
                    ackPhoneCall(phoneCallAck);
                    return;
                }

                logDebug('phoneCallAck', {
                    message: 'no action performed, need investigation',
                    phoneCallAck,
                });
                ackPhoneCall(phoneCallAck);
                return;
            },
        });
    }, [
        shouldHandleInCadNextWindow,
        workspaceWindowType,
        callTakerStation.currentStation?.value,
        phoneCallsStore,
        openEventPanelOnE911Call,
        ackPhoneCall,
        trigger,
        focusEventPanelOnE911Call,
        limitWph2RebidEnabled,
        numOpenEventPanels,
        openEventPanel,
        focusMapDuringCallCreation,
        viewCallOnMap,
        isFirstResponder,
        isWeb,
        isElectron,
        sendAssociatedPhoneNumberIdToMap,
    ]);

    useStream(phoneCallsListener);
    useStream(amlCallsListener);

    return (
        <PhoneCallsContext.Provider value={phoneCallsStore}>
            {props.children}
        </PhoneCallsContext.Provider>
    );
}
